<script setup lang="ts">
import type { MessagesComponents as components } from '@core/types'
import { computed, shallowRef, useDialogSequenceHold, useNotificationDialogs, watch } from '#imports'
import { notificationDialogComponent } from './dialogComponents'

const currentDialog = shallowRef<components['schemas']['RenderedNotification']>()
const currentOpened = shallowRef(false)
const notificationDialogs = useNotificationDialogs()
const { dialogs, status } = notificationDialogs
useDialogSequenceHold(notificationDialogs, {
    step: 'notification',
    when: () => !!currentDialog.value
})

const closedIds = new Set<string>()

// Once its sheet has opened, the notification stays until the sheet has closed, even once it left the queue, so the step isn't unmounted while open.
// Until then it stays while it's still queued, and one that expired or was read elsewhere before its sheet opened never opens.
watch(dialogs, (dialogs) => {
    if (currentOpened.value) return

    currentDialog.value = dialogs.find(dialog => dialog.id === currentDialog.value?.id)
        ?? dialogs.find(dialog => !closedIds.has(dialog.id))
}, {
    immediate: true
})

function onNotificationClosed() {
    if (currentDialog.value?.id) closedIds.add(currentDialog.value.id)
    currentOpened.value = false
    currentDialog.value = dialogs.value.find(dialog => !closedIds.has(dialog.id))
}

const currentComponent = computed(() => {
    if (!currentDialog.value) return

    return notificationDialogComponent(currentDialog.value.type)
})

const isProcessing = computed(() => {
    if (status.value === 'pending') return true

    return !!dialogs.value.length
})

defineExpose({
    isProcessing
})
</script>

<template>
    <component
        :is="currentComponent"
        v-if="currentComponent"
        :key="currentDialog!.id"
        :notification="currentDialog"
        @opened="currentOpened = true"
        @closed="onNotificationClosed"
    />
</template>
